import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Legacy bearer tokens are no longer used — scrub them.
try {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_user");
} catch (e) {
  console.warn("localStorage unavailable", e);
}

// Flash Report VAT display mode — appended to every public request when gross.
let _vatMode = "net";
export const setVatMode = (m) => { _vatMode = m === "gross" ? "gross" : "net"; };

api.interceptors.request.use((cfg) => {
  if (_vatMode === "gross" && String(cfg.url || "").startsWith("/public/")) {
    cfg.params = { ...(cfg.params || {}), vat: "gross" };
  }
  return cfg;
});

// Access cookie lasts 15 min: on 401, rotate via the refresh cookie once (single-flight) and retry.
let _refreshing = null;
const refreshSession = () => {
  if (!_refreshing) {
    _refreshing = axios.post(`${API}/auth/admin/refresh`, null, { withCredentials: true })
      .finally(() => { setTimeout(() => { _refreshing = null; }, 0); });
  }
  return _refreshing;
};

const isPublicPage = () => /^\/(c|flash)\//.test(window.location.pathname);

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config || {};
    const url = String(cfg.url || "");
    const authCall = url.startsWith("/auth/admin/login") || url.startsWith("/auth/admin/refresh") || url.startsWith("/auth/admin/logout");
    if (err?.response?.status === 401 && !cfg._retried && !authCall && !url.startsWith("/public/")) {
      cfg._retried = true;
      try {
        await refreshSession();
        return api(cfg);
      } catch {
        if (!cfg._silentAuth && !isPublicPage() && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

export const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n || 0);

export const fmtGBP2 = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

export const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

/** Robust clipboard copy that falls back to a hidden textarea + execCommand
 *  when navigator.clipboard is blocked (e.g. inside cross-origin iframes). */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    throw new Error("clipboard-api-unavailable");
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

export const fmtDateUK = (iso) => {
  if (!iso) return "—";
  // Accepts YYYY-MM-DD or full ISO; returns DD-MM-YY
  const s = String(iso).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}-${m}-${y.slice(2)}`;
};

const _DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const _MON_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Long UK date used for day-bucketed views — e.g. "Mon 03 Feb 2026". */
export const fmtDayLongUK = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts.map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return iso;
  return `${_DAY_ABBR[dt.getDay()]} ${String(d).padStart(2, "0")} ${_MON_ABBR[m - 1]} ${y}`;
};
